import { useEffect, useState } from "react";
import { API } from "../App";
import { Card, CardContent } from "./ui/card";
import { Button } from "./ui/button";
import { toast } from "sonner";
import { Award, Download, Linkedin, Share2, ShieldCheck } from "lucide-react";

const LinkedInShareButton = ({ cert }) => {
  const verifyUrl = `${window.location.origin}/verify/${cert.verification_id}`;
  const issuedAt = new Date(cert.issued_at);
  const params = new URLSearchParams({
    name: cert.course_name,
    organizationName: "Aspiritech Academy",
    issueYear: String(issuedAt.getFullYear()),
    issueMonth: String(issuedAt.getMonth() + 1),
    certUrl: verifyUrl,
    certId: cert.verification_id,
  });
  return (
    <a
      href={`https://www.linkedin.com/profile/add?${params.toString()}`}
      target="_blank"
      rel="noopener noreferrer"
      className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm rounded-lg border border-[#0A66C2] text-[#0A66C2] hover:bg-[#0A66C2]/5"
      data-testid={`linkedin-share-${cert.verification_id}`}
    >
      <Linkedin className="w-4 h-4" /> Add to LinkedIn
    </a>
  );
};

const CertificateCard = ({ cert }) => {
  const handleDownload = async () => {
    try {
      const res = await fetch(`${API}/certifications/${cert.certification_id}/pdf`, { credentials: "include" });
      if (!res.ok) {
        toast.error("Failed to download certificate");
        return;
      }
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `aspiritech-certificate-${cert.verification_id}.pdf`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch {
      toast.error("Network error");
    }
  };

  const verifyUrl = `${window.location.origin}/verify/${cert.verification_id}`;

  const handleCopyLink = () => {
    navigator.clipboard.writeText(verifyUrl);
    toast.success("Verification link copied!");
  };

  return (
    <Card className="card-surface border-2 border-[#FFD507]/40 bg-gradient-to-br from-[#FFD507]/5 to-white" data-testid={`certificate-${cert.verification_id}`}>
      <CardContent className="pt-5 pb-5">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div className="flex items-start gap-3 flex-1 min-w-[250px]">
            <div className="w-14 h-14 rounded-2xl bg-[#FFD507] flex items-center justify-center shrink-0 shadow-md">
              <ShieldCheck className="w-7 h-7 text-[#1E3A8A]" />
            </div>
            <div>
              <p className="text-xs uppercase tracking-wider text-[#1E3A8A] font-bold">Aspiritech Verified</p>
              <h3 className="text-lg font-heading font-bold text-slate-900 mt-0.5">{cert.course_name}</h3>
              <p className="text-sm text-slate-500 mt-1">
                Issued {new Date(cert.issued_at).toLocaleDateString()} by {cert.issued_by_name}
              </p>
              <p className="text-xs text-slate-400 mt-1 font-mono">ID: {cert.verification_id}</p>
            </div>
          </div>
          <div className="flex flex-wrap gap-2">
            <Button variant="outline" size="sm" onClick={handleDownload} className="flex items-center gap-1.5" data-testid={`download-${cert.verification_id}`}>
              <Download className="w-4 h-4" /> PDF
            </Button>
            <Button variant="outline" size="sm" onClick={handleCopyLink} className="flex items-center gap-1.5">
              <Share2 className="w-4 h-4" /> Copy verify link
            </Button>
            <LinkedInShareButton cert={cert} />
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

const BadgePill = ({ badge }) => (
  <div
    className="flex flex-col items-center text-center p-3 rounded-xl border-2 border-[#FFD507]/30 bg-white hover:shadow-md transition-shadow"
    data-testid={`badge-${badge.slug}`}
    title={`${badge.name} — ${badge.description}`}
  >
    <div className="text-3xl mb-1">{badge.emoji}</div>
    <p className="text-xs font-semibold text-slate-900">{badge.name}</p>
  </div>
);

const BadgesAndCerts = ({ userId, isOwn = true }) => {
  const [certs, setCerts] = useState([]);
  const [badges, setBadges] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      try {
        const certEndpoint = isOwn
          ? `${API}/users/me/certifications`
          : `${API}/users/${userId}/certifications`;
        const badgeEndpoint = isOwn
          ? `${API}/users/me/badges`
          : `${API}/users/${userId}/badges`;
        const [cRes, bRes] = await Promise.all([
          fetch(certEndpoint, { credentials: "include" }),
          fetch(badgeEndpoint, { credentials: "include" }),
        ]);
        if (cRes.ok) setCerts(await cRes.json());
        if (bRes.ok) setBadges(await bRes.json());
      } catch (e) { console.error(e); }
      setLoading(false);
    };
    load();
  }, [userId, isOwn]);

  if (loading) return null;

  return (
    <div className="space-y-6" data-testid="badges-and-certs">
      {/* Certifications - the BIG thing */}
      {certs.length > 0 && (
        <div>
          <div className="flex items-center gap-2 mb-3">
            <Award className="w-5 h-5 text-[#FFD507]" />
            <h2 className="text-lg font-heading font-bold text-slate-900">Verified Certifications</h2>
          </div>
          <div className="space-y-3">
            {certs.map((c) => <CertificateCard key={c.certification_id} cert={c} />)}
          </div>
        </div>
      )}

      {/* Badges */}
      {badges.length > 0 && (
        <div>
          <div className="flex items-center gap-2 mb-3">
            <span className="text-lg">🏆</span>
            <h2 className="text-lg font-heading font-bold text-slate-900">Achievements ({badges.length})</h2>
          </div>
          <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 gap-3">
            {badges.map((b) => <BadgePill key={b.slug} badge={b} />)}
          </div>
        </div>
      )}

      {certs.length === 0 && badges.length === 0 && isOwn && (
        <Card className="card-surface bg-slate-50">
          <CardContent className="pt-6 pb-6 text-center text-slate-500">
            <Award className="w-10 h-10 mx-auto mb-2 text-slate-300" />
            <p className="text-sm">No achievements yet. Complete your profile, finish the Working Style Quiz, and upload your documents to start earning badges!</p>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default BadgesAndCerts;
